import React, { memo, useCallback, FC } from 'react';
import MuiCheckbox, { CheckboxProps as MuiCheckboxProps } from '@mui/material/Checkbox';
import { mergeClassNames } from '../../utils/cssClassNameHelper';
import { FormControlLabel, FormControlLabelProps } from '../Form/FormControlLabel';
import { Typography } from '../Typography';
import { BpDesignSystem } from '../../types';
import { useBpDesignTheme } from '../../setup';
import { styled } from '../../styles';
import { reactNodeToString } from '../../utils/reactNode';
import { FormHelperText } from '../Form/FormHelperText';
import { FormGroup } from '../Form/FormGroup';

export type CheckboxProps = BpDesignSystem.PropsWithChildren<
  MuiCheckboxProps & {
    onClick?: (e: React.MouseEvent) => void;
    onFocus?: (e: React.FocusEvent) => void;
    dashStyle?: boolean;
    label?: React.ReactNode;
    subLabel?: React.ReactNode;
    dense?: boolean;
    errorText?: string;
    helperText?: string;
    error?: boolean;
    labelPlacement?: FormControlLabelProps['labelPlacement'];
    slotProps?: {
      input?: React.InputHTMLAttributes<HTMLInputElement>;
      checkbox?: MuiCheckboxProps;
    };
    fullWidth?: boolean;
    align?: 'top' | 'center' | 'bottom';
  }
>;

export const Checkbox: FC<CheckboxProps> = memo(
  ({
    onChange,
    onFocus,
    onClick,
    tabIndex,
    name,
    required,
    labelPlacement = 'end',
    label,
    subLabel,
    disabled,
    error,
    className,
    style,
    helperText,
    errorText,
    sx,
    checked,
    children,
    dashStyle,
    dataTest,
    id,
    dense,
    slotProps,
    fullWidth,
    align = 'center',
    ...props
  }) => {
    const denseToSet = dense ?? true;
    const classNameToSet = mergeClassNames(className, {
      disabled: disabled,
      checked: !!checked,
      dense: denseToSet
    });
    const { tokens } = useBpDesignTheme();
    const isChecked = checked || dashStyle;
    const disableFontColor = disabled ? tokens.stateOnSurfaceDisabledContentStateLayerColor : undefined;

    const handleChange = useCallback(
      (event: React.SyntheticEvent, checked: boolean) => {
        onChange?.(event as unknown as React.ChangeEvent<HTMLInputElement>, checked);
      },
      [onChange]
    );

    return (
      <StyledFormGroup style={style} sx={sx} className={classNameToSet}>
        <StyledFormControlLabel
          dataTest={dataTest}
          id={id}
          $fullWidth={fullWidth}
          required={required}
          style={{ padding: 0 }}
          labelPlacement={labelPlacement}
          disabled={disabled}
          onClick={onClick}
          onFocus={onFocus}
          onChange={handleChange}
          className={`bp-align-${align} bp-label-placement-${labelPlacement}`}
          control={
            <MuiCheckbox
              indeterminate={dashStyle ?? false}
              checked={isChecked ?? false}
              data-test={`${dataTest}-checkbox`}
              inputProps={{
                'aria-label': children ? reactNodeToString(children) : 'checkbox',
                role: 'checkbox',
                ...slotProps?.input,
                style: { padding: 0, ...slotProps?.input?.style }
              }}
              id={id ? `${id}-input` : undefined}
              disabled={disabled ?? false}
              aria-checked={typeof checked === 'undefined' ? undefined : (checked.toString() as 'true' | 'false')}
              color="primary"
              tabIndex={tabIndex}
              name={name}
              required={required}
              {...props}
              {...slotProps?.checkbox}
            />
          }
          label={
            <>
              <StyledLabel
                className={`label ${checked ? 'checked' : 'unChecked'}`}
                $error={!!error}
                variant="label1"
                color={!disabled ? (error ? tokens.colorAlert : undefined) : disableFontColor}
                dataTest="checkbox-label"
              >
                {label || children}
              </StyledLabel>
              {!!subLabel && (
                <StyledTypography variant="body" color={!disabled ? undefined : disableFontColor}>
                  {subLabel}
                </StyledTypography>
              )}
            </>
          }
        />
        <FormHelperText helperText={helperText} errorText={errorText} error={error} />
      </StyledFormGroup>
    );
  }
);

const StyledFormControlLabel = styled(FormControlLabel)<{ $fullWidth?: boolean }>`
  && {
    .MuiFormControlLabel-label {
      width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
    }
  }
`;

const StyledLabel = styled(Typography)<{ $error: boolean }>`
    ${({ $error, theme }) => ($error ? `& * { color: ${theme.tokens.colorAlert} }` : ``)}
`;

const StyledFormGroup = styled(FormGroup)`
    margin: ${({ theme }) => theme.spacingSize.m};
    .MuiFormHelperText-root {
        margin-left: ${({ theme }) => theme.spacingSize.ml}
    }

    .MuiFormControlLabel-root {
        margin-left: 0;
    }

    .MuiCheckbox-root + div {
        position: relative
    }

    .MuiFormControlLabel-asterisk {
        position: absolute;
        top: -6px;
        right: 0;
    }

    .typography-label {
        margin: 0 ${({ theme }) => theme.spacingSize.m};
    }

    .Mui-disabled {
        .MuiSvgIcon-root {
            fill: ${({ theme }) => theme.tokens.stateOnSurfaceDisabledContainerStateLayerColor};
        }
    }

`;

const StyledTypography = styled(Typography)`
  && {
    margin-top: 2px;
  }
`;
